import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { formatLocal } from '../utils/timezones';
import { useHOA } from '../HOAApp';

const AnnouncementsTab = ({ user }) => {
  const { hoaData } = useHOA();
  const hoaTimezone = hoaData?.timezone || 'US/Central';
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Form states
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [announcementType, setAnnouncementType] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [sendEmail, setSendEmail] = useState(false);
  const [projectCost, setProjectCost] = useState('');
  const [projectDetails, setProjectDetails] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [attachedImages, setAttachedImages] = useState([]);
  const [attachedDocuments, setAttachedDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Check if user is board member
  const isBoardMember = () => {
    return ['board_member', 'president', 'vice_president', 'treasurer', 'secretary'].includes(user?.role?.toLowerCase());
  };

  useEffect(() => {
    if (user?.hoa_id) {
      loadAnnouncements();
    }
  }, [user?.hoa_id]);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/announcements?limit=50`, {
        headers: {
          'X-HOA-ID': user?.hoa_id
        }
      });
      setAnnouncements(data.announcements || []);
    } catch (error) {
      console.error('Error loading announcements:', error);
      alert('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!title.trim() || !content.trim()) {
      alert('Please fill in title and content');
      return;
    }

    try {
      const announcementData = {
        title: title.trim(),
        content: content.trim(),
        announcement_type: announcementType,
        priority,
        send_email: sendEmail,
        project_cost: projectCost ? parseFloat(projectCost) : null,
        project_details: projectDetails.trim(),
        effective_date: effectiveDate || null,
        expiration_date: expirationDate || null,
        attached_images: attachedImages,
        attached_documents: attachedDocuments.map(d => d.file_id)
      };

      console.log('[ANNOUNCEMENT DEBUG] Sending data:', announcementData);
      console.log('[ANNOUNCEMENT DEBUG] sendEmail state value:', sendEmail);

      const response = await api.post(`/api/announcements`, announcementData, {
        headers: {
          'X-HOA-ID': user.hoa_id
        }
      });
      
      console.log('[ANNOUNCEMENT DEBUG] Response:', response.data);
      
      alert(`Announcement created successfully!${sendEmail ? ' Email notifications sent to all residents.' : ''}`);
      
      // Reset form
      setTitle('');
      setContent('');
      setAnnouncementType('general');
      setPriority('normal');
      setSendEmail(false);
      setProjectCost('');
      setProjectDetails('');
      setEffectiveDate('');
      setExpirationDate('');
      setAttachedImages([]);
      setAttachedDocuments([]);
      setShowCreateModal(false);
      
      // Reload announcements
      loadAnnouncements();
    } catch (error) {
      console.error('Error creating announcement:', error);
      alert('Failed to create announcement. Please try again.');
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file has extension
    if (!file.name.includes('.')) {
      alert('⚠️ Image file must have an extension!\n\nYour file: "' + file.name + '"\n\nPlease rename to include extension:\n• .jpg for photos\n• .png for screenshots\n• .gif for animations\n• .webp for web images\n\nOR use "Attach Document" section below for files without extensions.');
      event.target.value = '';
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, GIF, or WEBP)\n\nFor other file types, use "Attach Document" section below.');
      event.target.value = '';
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('Image file is too large. Maximum size is 10MB.');
      event.target.value = '';
      return;
    }

    console.log('📸 Uploading image:', {
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024).toFixed(2)} KB`,
      hoaId: user.hoa_id
    });

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await api.post(`/api/announcements/upload-image`, formData, {
        headers: {
          'X-HOA-ID': user.hoa_id
        }
      });

      console.log('✅ Image uploaded successfully:', data);
      setAttachedImages(prev => [...prev, data.file_id]);
      alert('✅ Image uploaded successfully!');
    } catch (error) {
      console.error('❌ Error uploading image:', error);
      console.error('Error response:', error.response);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to upload image:\n\n${errorMsg}\n\nPlease check:\n- File has proper extension (.jpg, .png, .gif, .webp)\n- File size is under 10MB\n- You have permission to upload`);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (max 25MB for documents)
    const maxSize = 25 * 1024 * 1024; // 25MB
    if (file.size > maxSize) {
      alert('Document file is too large. Maximum size is 25MB.');
      event.target.value = '';
      return;
    }

    console.log('📎 Uploading document:', {
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024).toFixed(2)} KB`,
      hoaId: user.hoa_id
    });

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await api.post(`/api/announcements/upload-document`, formData, {
        headers: {
          'X-HOA-ID': user.hoa_id
        }
      });

      console.log('✅ Document uploaded successfully:', data);
      setAttachedDocuments(prev => [...prev, { file_id: data.file_id, filename: data.original_name }]);
      alert('✅ Document uploaded successfully!');
    } catch (error) {
      console.error('❌ Error uploading document:', error);
      console.error('Error response:', error.response);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to upload document:\n\n${errorMsg}\n\nPlease check:\n- File size is under 25MB\n- You have permission to upload`);
    } finally {
      setUploadingDoc(false);
      event.target.value = '';
    }
  };

  const removeImage = (index) => {
    setAttachedImages(prev => prev.filter((_, i) => i !== index));
  };

  const viewAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowDetailModal(true);
  };

  const getAnnouncementIcon = (type) => {
    const icons = {
      general: '📢',
      project: '🏗️',
      voting: '🗳️',
      emergency: '🚨'
    };
    return icons[type] || '📢';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      normal: 'bg-gray-100 text-gray-800',
      high: 'bg-yellow-100 text-yellow-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.normal;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Community Announcements</h2>
          <p className="text-gray-600">Stay informed about important community updates and notices</p>
        </div>
        {isBoardMember() && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Announcement
          </button>
        )}
      </div>

      {/* Announcements List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading announcements...</p>
        </div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No announcements</h3>
          <p className="mt-1 text-sm text-gray-500">
            {isBoardMember() 
              ? 'Get started by creating your first announcement.'
              : 'Check back later for community updates.'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => (
            <div key={announcement.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-3">{getAnnouncementIcon(announcement.announcement_type)}</span>
                    <h3 className="text-lg font-semibold text-gray-900">{announcement.title}</h3>
                    <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(announcement.priority)}`}>
                      {announcement.priority.toUpperCase()}
                    </span>
                  </div>
                  
                  <p className="text-gray-700 mb-3 line-clamp-2">{announcement.content}</p>
                  
                  {announcement.project_cost && (
                    <div className="mb-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-green-100 text-green-800">
                        💰 Estimated Cost: ${announcement.project_cost.toLocaleString()}
                      </span>
                    </div>
                  )}
                  
                  {(announcement.attached_images?.length > 0 || announcement.attached_documents?.length > 0) && (
                    <div className="mb-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-blue-100 text-blue-800">
                        📎 {(announcement.attached_images?.length || 0) + (announcement.attached_documents?.length || 0)} attachment(s)
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <span>By {announcement.created_by_name}</span>
                    <span className="mx-2">•</span>
                    <span>{formatLocal(announcement.created_at, hoaTimezone, 'MMM d, yyyy')}</span>
                    {announcement.view_count > 0 && (
                      <>
                        <span className="mx-2">•</span>
                        <span>{announcement.view_count} views</span>
                      </>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={() => viewAnnouncement(announcement)}
                  className="ml-4 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Announcement Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Create New Announcement</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter announcement title..."
                    maxLength={200}
                  />
                </div>

                {/* Type and Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={announcementType}
                      onChange={(e) => setAnnouncementType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="general">📢 General</option>
                      <option value="project">🏗️ Project</option>
                      <option value="voting">🗳️ Voting</option>
                      <option value="emergency">🚨 Emergency</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content *
                  </label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter announcement details..."
                  />
                </div>

                {/* Project Details (if project type) */}
                {announcementType === 'project' && (
                  <div className="space-y-4 p-4 bg-blue-50 rounded-md">
                    <h4 className="font-medium text-blue-900">Project Information</h4>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">
                        Estimated Cost
                      </label>
                      <input
                        type="number"
                        value={projectCost}
                        onChange={(e) => setProjectCost(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">
                        Additional Project Details
                      </label>
                      <textarea
                        value={projectDetails}
                        onChange={(e) => setProjectDetails(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Contractor information, timeline, materials, etc..."
                      />
                    </div>
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Effective Date
                    </label>
                    <input
                      type="date"
                      value={effectiveDate}
                      onChange={(e) => setEffectiveDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiration Date
                    </label>
                    <input
                      type="date"
                      value={expirationDate}
                      onChange={(e) => setExpirationDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attach Images
                  </label>
                  <p className="text-xs text-gray-600 mb-2">
                    ⚠️ Images must have file extensions (.jpg, .png, .gif, .webp). 
                    If your file has no extension, use "Attach Documents" below.
                  </p>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                      disabled={uploading}
                    />
                    <label
                      htmlFor="image-upload"
                      className={`cursor-pointer flex flex-col items-center ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="mt-2 text-sm text-gray-500">
                        {uploading ? 'Uploading...' : 'Click to upload images'}
                      </span>
                    </label>
                    
                    {attachedImages.length > 0 && (
                      <div className="mt-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Uploaded Images:</h5>
                        <div className="space-y-2">
                          {attachedImages.map((imageId, index) => (
                            <div key={index} className="flex items-center justify-between bg-green-50 p-2 rounded">
                              <span className="text-sm text-green-800">Image {index + 1} uploaded</span>
                              <button
                                onClick={() => removeImage(index)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Document Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attach Documents
                  </label>
                  <p className="text-xs text-gray-600 mb-2">
                    📎 PDFs, Word docs, Excel, images without extensions, etc. (Max 25MB)
                  </p>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <input
                      type="file"
                      onChange={handleDocumentUpload}
                      className="hidden"
                      id="document-upload"
                      disabled={uploadingDoc}
                    />
                    <label
                      htmlFor="document-upload"
                      className={`cursor-pointer flex flex-col items-center ${uploadingDoc ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="mt-2 text-sm text-gray-500">
                        {uploadingDoc ? 'Uploading...' : 'Click to upload documents'}
                      </span>
                    </label>
                    
                    {attachedDocuments.length > 0 && (
                      <div className="mt-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Uploaded Documents:</h5>
                        <div className="space-y-2">
                          {attachedDocuments.map((doc, index) => (
                            <div key={index} className="flex items-center justify-between bg-green-50 p-2 rounded">
                              <span className="text-sm text-green-800">{doc.filename}</span>
                              <button
                                onClick={() => setAttachedDocuments(prev => prev.filter((_, i) => i !== index))}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Email Notification */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="send-email"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="send-email" className="ml-2 block text-sm text-gray-700">
                    Send email notification to all residents
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAnnouncement}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create Announcement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedAnnouncement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center">
                  <span className="text-3xl mr-3">{getAnnouncementIcon(selectedAnnouncement.announcement_type)}</span>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{selectedAnnouncement.title}</h3>
                    <div className="flex items-center mt-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(selectedAnnouncement.priority)}`}>
                        {selectedAnnouncement.priority.toUpperCase()}
                      </span>
                      <span className="ml-3 text-sm text-gray-500">
                        {selectedAnnouncement.announcement_type.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Content */}
                <div className="prose max-w-none">
                  <div className="whitespace-pre-wrap text-gray-700">
                    {selectedAnnouncement.content}
                  </div>
                </div>

                {/* Project Information */}
                {selectedAnnouncement.announcement_type === 'project' && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Project Information</h4>
                    {selectedAnnouncement.project_cost && (
                      <p className="text-blue-800 mb-2">
                        <strong>Estimated Cost:</strong> ${selectedAnnouncement.project_cost.toLocaleString()}
                      </p>
                    )}
                    {selectedAnnouncement.project_details && (
                      <p className="text-blue-800">
                        <strong>Details:</strong> {selectedAnnouncement.project_details}
                      </p>
                    )}
                  </div>
                )}

                {/* Dates */}
                {(selectedAnnouncement.effective_date || selectedAnnouncement.expiration_date) && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Important Dates</h4>
                    {selectedAnnouncement.effective_date && (
                      <p className="text-gray-700 mb-1">
                        <strong>Effective:</strong> {formatLocal(selectedAnnouncement.effective_date, hoaTimezone, 'MMM d, yyyy')}
                      </p>
                    )}
                    {selectedAnnouncement.expiration_date && (
                      <p className="text-gray-700">
                        <strong>Expires:</strong> {formatLocal(selectedAnnouncement.expiration_date, hoaTimezone, 'MMM d, yyyy')}
                      </p>
                    )}
                  </div>
                )}

                {/* Images */}
                {selectedAnnouncement.attached_images?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Attached Images</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedAnnouncement.attached_images.map((imageId, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden">
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/announcements/files/${imageId}?hoa_id=${user?.hoa_id}`}
                            alt={`Attachment ${index + 1}`}
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                              console.error('Failed to load image:', imageId);
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage Error%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents */}
                {selectedAnnouncement.attached_documents?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Attached Documents</h4>
                    <div className="space-y-2">
                      {selectedAnnouncement.attached_documents.map((doc, index) => (
                        <div key={index} className="flex items-center p-3 border rounded-lg hover:bg-gray-50">
                          <svg className="h-8 w-8 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{doc.original_name || `Document ${index + 1}`}</p>
                            <p className="text-xs text-gray-500">Click to download</p>
                          </div>
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/announcements/files/${doc.file_id}?hoa_id=${user?.hoa_id}`}
                            download
                            className="ml-3 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Download
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Meta Information */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div>
                      <span>Posted by {selectedAnnouncement.created_by_name}</span>
                      <span className="mx-2">•</span>
                      <span>{formatLocal(selectedAnnouncement.created_at, hoaTimezone, 'MMM d, yyyy')}</span>
                    </div>
                    {selectedAnnouncement.view_count > 0 && (
                      <span>{selectedAnnouncement.view_count} views</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsTab;